import { useState } from "react";
import { Button } from "../../primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../primitives/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Textarea } from "../../primitives/textarea";
import { SNOOZE_DURATION_PRESETS } from "@shared-core/domain/alert-snooze";
import type * as API from "@shared-core/data/api-types";

type SnoozeDurationPreset = NonNullable<
  API.AlertUpdateRequest["snooze_duration"]
>;

interface SnoozeAlertControlProps {
  onSnooze: (durationPreset: string, reason?: string) => void;
  size?: "sm" | "default";
}

export function SnoozeAlertControl({
  onSnooze,
  size = "sm",
}: SnoozeAlertControlProps) {
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState<SnoozeDurationPreset | "">("");
  const [reason, setReason] = useState("");

  const handleConfirm = () => {
    if (!duration) return;
    onSnooze(duration, reason.trim() || undefined);
    setOpen(false);
    setDuration("");
    setReason("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={size}
          onClick={(e) => e.stopPropagation()}
        >
          Snooze
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80"
        onClick={(e) => e.stopPropagation()}
        align="end"
      >
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Snooze for
            </label>
            <Select
              value={duration}
              onValueChange={(v) => setDuration(v as SnoozeDurationPreset)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a duration" />
              </SelectTrigger>
              <SelectContent>
                {SNOOZE_DURATION_PRESETS.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Note (optional)
            </label>
            <Textarea
              placeholder="e.g. In conversation on a longer project, revisit in 2 months"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={!duration} onClick={handleConfirm}>
              Snooze
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
